import { create } from 'zustand'
import api from '../services/api'
import toast from 'react-hot-toast'

export const useChatStore = create((set, get) => ({
  // State
  chats: [],
  currentChat: null,
  messages: [],
  onlineUsers: [],
  typingUsers: new Map(),
  isLoading: false,
  error: null,
  hasMoreMessages: true,
  currentPage: 1,

  // Actions
  setChats: (chats) => set({ chats }),
  setCurrentChat: (chat) => set((state) => {
    // Only reset messages if switching to a different chat
    const isDifferentChat = state.currentChat?._id !== chat?._id;
    return {
      currentChat: chat,
      messages: isDifferentChat ? [] : state.messages,
      currentPage: isDifferentChat ? 1 : state.currentPage,
      hasMoreMessages: isDifferentChat ? true : state.hasMoreMessages
    };
  }),
  setMessages: (messages) => set({ messages }),
  addMessage: (message) => set((state) => {
    // Check if message already exists to prevent duplicates
    const messageExists = state.messages.some(msg => msg._id === message._id)
    if (messageExists) {
      console.log('🔄 Message already exists, skipping duplicate:', message._id)
      return state
    }
    console.log('➕ Adding new message:', message._id)
    return { messages: [...state.messages, message] }
  }),
  updateMessage: (messageId, updates) => set((state) => ({
    messages: state.messages.map(msg => 
      msg._id === messageId ? { ...msg, ...updates } : msg
    )
  })),
  removeMessage: (messageId) => set((state) => ({
    messages: state.messages.filter(msg => msg._id !== messageId)
  })),
  setOnlineUsers: (users) => set({ onlineUsers: users }),
  setTypingUsers: (typingUsers) => set({ typingUsers }),
  setLoading: (isLoading) => set({ isLoading }),
  setError: (error) => set({ error }),

  // Get specific chat details
  fetchChatDetails: async (chatId) => {
    try {
      const response = await api.get(`/chats/${chatId}`)
      const { chat } = response.data
      return chat
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to fetch chat details'
      console.error('Fetch chat details error:', error)
      return null
    }
  },

  // Fetch user's chats
  fetchChats: async () => {
    set({ isLoading: true, error: null })
    try {
      const response = await api.get('/chats')
      const { chats } = response.data
      set({ chats, isLoading: false })
      return chats
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to fetch chats'
      set({ error: message, isLoading: false })
      toast.error(message)
      return null
    }
  },

  // Create new chat
  createChat: async (participants, isGroupChat = false, name = '', description = '') => {
    set({ isLoading: true, error: null })
    try {
      const payload = { participants, isGroupChat }
      if (isGroupChat && name) payload.name = name
      if (description) payload.description = description

      const response = await api.post('/chats', payload)
      const { chat } = response.data
      
      set((state) => ({
        chats: [chat, ...state.chats],
        isLoading: false
      }))
      
      toast.success(isGroupChat ? 'Group created successfully!' : 'Chat created!')
      return chat
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to create chat'
      set({ error: message, isLoading: false })
      toast.error(message)
      return null
    }
  },

  // Fetch messages for a chat
  fetchMessages: async (chatId, page = 1, search = '') => {
    console.log('🔄 fetchMessages called with:', { chatId, page, search })
    set({ isLoading: page === 1, error: null })
    try {
      const params = { page, limit: 50 }
      if (search) params.search = search

      console.log('📡 Making API request to:', `/messages/${chatId}`, 'with params:', params)
      const response = await api.get(`/messages/${chatId}`, { params })
      const { messages, pagination } = response.data
      
      console.log('📝 API response:', { 
        messagesCount: messages.length, 
        pagination,
        messages: messages.map(m => ({ id: m._id, content: m.content, sender: m.sender?.username }))
      })
      
      if (page === 1) {
        console.log('✅ Setting messages for page 1')
        set({ 
          messages, 
          currentPage: 1,
          hasMoreMessages: pagination.pages > 1,
          isLoading: false 
        })
      } else {
        console.log('✅ Appending messages for page', page)
        set((state) => ({
          messages: [...messages.reverse(), ...state.messages],
          currentPage: page,
          hasMoreMessages: page < pagination.pages,
          isLoading: false
        }))
      }
      
      return messages
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to fetch messages'
      console.error('❌ Fetch messages error:', error)
      console.error('❌ Error response:', error.response?.data)
      set({ error: message, isLoading: false })
      return null
    }
  },

  // Send message
  sendMessage: async (chatId, content, messageType = 'text', attachments = null, replyTo = null) => {
    try {
      const payload = { chatId, messageType }
      if (content) payload.content = content
      if (attachments) payload.attachments = attachments
      if (replyTo) payload.replyTo = replyTo

      const response = await api.post('/messages', payload)
      const { message } = response.data
      
      // Message will be added via socket event
      return message
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to send message'
      toast.error(message)
      return null
    }
  },

  // Edit message
  editMessage: async (messageId, content) => {
    try {
      const response = await api.put(`/messages/${messageId}`, { content })
      const { message } = response.data
      
      get().updateMessage(messageId, message)
      toast.success('Message updated!')
      return message
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to edit message'
      toast.error(message)
      return null
    }
  },

  // Delete message
  deleteMessage: async (messageId) => {
    try {
      await api.delete(`/messages/${messageId}`)
      get().updateMessage(messageId, { isDeleted: true, content: '', attachments: [] })
      toast.success('Message deleted!')
      return true
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to delete message'
      toast.error(message)
      return false
    }
  },

  // Toggle message reaction
  toggleReaction: async (messageId, emoji) => {
    try {
      const response = await api.post(`/messages/${messageId}/reactions`, { emoji })
      const { message } = response.data
      
      get().updateMessage(messageId, { reactions: message.reactions })
      return message
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to toggle reaction'
      toast.error(message)
      return null
    }
  },

  // Search messages
  searchMessages: async (query, page = 1) => {
    set({ isLoading: true, error: null })
    try {
      const response = await api.get('/messages/search/global', {
        params: { q: query, page, limit: 20 }
      })
      const { messages, pagination } = response.data
      
      set({ isLoading: false })
      return { messages, pagination }
    } catch (error) {
      const message = error.response?.data?.message || 'Search failed'
      set({ error: message, isLoading: false })
      toast.error(message)
      return null
    }
  },

  // Update chat
  updateChat: async (chatId, updates) => {
    try {
      const response = await api.put(`/chats/${chatId}`, updates)
      const { chat } = response.data
      
      set((state) => ({
        chats: state.chats.map(c => c._id === chatId ? chat : c),
        currentChat: state.currentChat?._id === chatId ? chat : state.currentChat
      }))
      
      toast.success('Chat updated successfully!')
      return chat
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to update chat'
      toast.error(message)
      return null
    }
  },

  // Add participants to group chat
  addParticipants: async (chatId, participants) => {
    try {
      const response = await api.post(`/chats/${chatId}/participants`, { participants })
      const { chat } = response.data
      
      set((state) => ({
        chats: state.chats.map(c => c._id === chatId ? chat : c),
        currentChat: state.currentChat?._id === chatId ? chat : state.currentChat
      }))
      
      toast.success('Participants added successfully!')
      return chat
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to add participants'
      toast.error(message)
      return null
    }
  },

  // Remove participant from group chat
  removeParticipant: async (chatId, userId) => {
    try {
      await api.delete(`/chats/${chatId}/participants/${userId}`)
      
      // Refresh chats
      get().fetchChats()
      toast.success('Participant removed successfully!')
      return true
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to remove participant'
      toast.error(message)
      return false
    }
  },

  // Delete chat
  deleteChat: async (chatId) => {
    try {
      await api.delete(`/chats/${chatId}`)
      
      set((state) => ({
        chats: state.chats.filter(c => c._id !== chatId),
        currentChat: state.currentChat?._id === chatId ? null : state.currentChat,
        messages: state.currentChat?._id === chatId ? [] : state.messages
      }))
      
      toast.success('Chat deleted successfully!')
      return true
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to delete chat'
      toast.error(message)
      return false
    }
  },

  // Search users
  searchUsers: async (query) => {
    console.log('🔍 searchUsers called with query:', query)
    if (!query || query.length < 2) {
      console.log('❌ Query validation failed')
      return []
    }
    
    try {
      console.log('📡 Making API request to /users/search...')
      const response = await api.get('/users/search', {
        params: { q: query, limit: 20 }
      })
      console.log('✅ API response:', response.data)
      return response.data.users || []
    } catch (error) {
      console.error('❌ Search users error:', error)
      console.error('Error details:', {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message
      })
      toast.error('Failed to search users')
      return []
    }
  },

  // Clear current chat
  clearCurrentChat: () => set({ 
    currentChat: null, 
    messages: [], 
    currentPage: 1, 
    hasMoreMessages: true 
  }),

  // Clear error
  clearError: () => set({ error: null }),
}))
